export interface ComposeSendPrompts {
    confirmDiscardUnsent: () => Promise<boolean>;
    confirmCloseWhileSending: () => Promise<boolean>;
}

export interface ComposeDismissGuard {
    confirmDismiss: () => Promise<boolean>;
}

type ComposeSendPhase = "idle" | "preparing" | "handedOff";

export class ComposeSendLifecycle implements ComposeDismissGuard {
    private phase: ComposeSendPhase = "idle";
    private pendingDiscard: Promise<boolean> | null = null;

    constructor(private readonly prompts: ComposeSendPrompts) {}

    begin(): void {
        this.phase = "preparing";
    }

    settle(): void {
        this.phase = "idle";
    }

    async handOff(): Promise<boolean> {
        const discarded = this.pendingDiscard ? await this.pendingDiscard : false;
        if (discarded || this.phase !== "preparing") {
            return false;
        }
        this.phase = "handedOff";
        return true;
    }

    async confirmDismiss(): Promise<boolean> {
        switch (this.phase) {
            case "idle":
                return true;
            case "preparing":
                return this.confirmDiscard();
            case "handedOff":
                return this.prompts.confirmCloseWhileSending();
            default: {
                const exhaustiveCheck: never = this.phase;
                throw new Error(`Unhandled send phase: ${exhaustiveCheck}`);
            }
        }
    }

    private async confirmDiscard(): Promise<boolean> {
        this.pendingDiscard = this.prompts.confirmDiscardUnsent();
        const discard = await this.pendingDiscard;
        this.pendingDiscard = null;
        if (discard) {
            this.settle();
        }
        return discard;
    }
}
